/**
 * theme.ts — D.2.2 MUI Theme Configuration
 *
 * Creates the PhiloFund MUI theme from design tokens extracted from Figma.
 * This file exports `philofundTheme` — a fully-configured MUI Theme object.
 *
 * Consumers:
 *   - App.tsx: wraps the tree with <ThemeProvider theme={philofundTheme}>
 *   - Tests: import directly to assert token values
 *
 * Design decisions:
 *   - textTransform: 'none' on buttons — friendlier tone for a philanthropy app
 *   - Dense spacing not used — generous whitespace improves trust/legibility
 *   - CssBaseline is NOT applied here; it is applied in App.tsx
 */
import { createTheme } from '@mui/material/styles';
import { COLOR, SHAPE, SPACING, TRANSITION, TYPOGRAPHY } from './tokens';

export const philofundTheme = createTheme({
  // ---- Palette ----------------------------------------------------------
  palette: {
    mode: 'light',

    primary: {
      main: COLOR.primary.main,
      dark: COLOR.primary.dark,
      light: COLOR.primary.light,
      contrastText: COLOR.primary.contrastText,
    },

    secondary: {
      main: COLOR.secondary.main,
      dark: COLOR.secondary.dark,
      light: COLOR.secondary.light,
      contrastText: COLOR.secondary.contrastText,
    },

    error: {
      main: COLOR.error.main,
      light: COLOR.error.light,
      dark: COLOR.error.dark,
      contrastText: COLOR.error.contrastText,
    },

    warning: {
      main: COLOR.warning.main,
      light: COLOR.warning.light,
      dark: COLOR.warning.dark,
      contrastText: COLOR.warning.contrastText,
    },

    info: {
      main: COLOR.info.main,
      light: COLOR.info.light,
      dark: COLOR.info.dark,
      contrastText: COLOR.info.contrastText,
    },

    success: {
      main: COLOR.success.main,
      light: COLOR.success.light,
      dark: COLOR.success.dark,
      contrastText: COLOR.success.contrastText,
    },

    background: {
      default: COLOR.background.default,
      paper: COLOR.background.paper,
    },

    text: {
      primary: COLOR.text.primary,
      secondary: COLOR.text.secondary,
      disabled: COLOR.text.disabled,
    },

    divider: COLOR.divider,
  },

  // ---- Typography -------------------------------------------------------
  typography: {
    fontFamily: TYPOGRAPHY.fontFamily.primary,
    fontSize: TYPOGRAPHY.fontSize.base,
    fontWeightLight: TYPOGRAPHY.fontWeight.light,
    fontWeightRegular: TYPOGRAPHY.fontWeight.regular,
    fontWeightMedium: TYPOGRAPHY.fontWeight.medium,
    fontWeightBold: TYPOGRAPHY.fontWeight.bold,

    h1: {
      fontSize: TYPOGRAPHY.fontSize['4xl'],
      fontWeight: TYPOGRAPHY.fontWeight.bold,
      lineHeight: TYPOGRAPHY.lineHeight.tight,
      letterSpacing: '-0.02em',
    },
    h2: {
      fontSize: TYPOGRAPHY.fontSize['3xl'],
      fontWeight: TYPOGRAPHY.fontWeight.semibold,
      lineHeight: TYPOGRAPHY.lineHeight.tight,
      letterSpacing: '-0.01em',
    },
    h3: {
      fontSize: TYPOGRAPHY.fontSize['2xl'],
      fontWeight: TYPOGRAPHY.fontWeight.semibold,
      lineHeight: TYPOGRAPHY.lineHeight.snug,
    },
    h4: {
      fontSize: TYPOGRAPHY.fontSize.xl,
      fontWeight: TYPOGRAPHY.fontWeight.semibold,
      lineHeight: TYPOGRAPHY.lineHeight.snug,
    },
    h5: {
      fontSize: TYPOGRAPHY.fontSize.lg,
      fontWeight: TYPOGRAPHY.fontWeight.medium,
      lineHeight: TYPOGRAPHY.lineHeight.normal,
    },
    h6: {
      fontSize: TYPOGRAPHY.fontSize.md,
      fontWeight: TYPOGRAPHY.fontWeight.medium,
      lineHeight: TYPOGRAPHY.lineHeight.normal,
    },

    body1: {
      fontSize: TYPOGRAPHY.fontSize.md,
      lineHeight: TYPOGRAPHY.lineHeight.relaxed,
      fontWeight: TYPOGRAPHY.fontWeight.regular,
    },
    body2: {
      fontSize: TYPOGRAPHY.fontSize.sm,
      lineHeight: TYPOGRAPHY.lineHeight.relaxed,
      fontWeight: TYPOGRAPHY.fontWeight.regular,
    },

    subtitle1: {
      fontSize: TYPOGRAPHY.fontSize.md,
      fontWeight: TYPOGRAPHY.fontWeight.medium,
      lineHeight: TYPOGRAPHY.lineHeight.normal,
    },
    subtitle2: {
      fontSize: TYPOGRAPHY.fontSize.sm,
      fontWeight: TYPOGRAPHY.fontWeight.medium,
      lineHeight: TYPOGRAPHY.lineHeight.normal,
    },

    caption: {
      fontSize: TYPOGRAPHY.fontSize.xs,
      lineHeight: TYPOGRAPHY.lineHeight.normal,
      fontWeight: TYPOGRAPHY.fontWeight.regular,
    },
    overline: {
      fontSize: TYPOGRAPHY.fontSize.xs,
      fontWeight: TYPOGRAPHY.fontWeight.medium,
      letterSpacing: '0.08em',
      textTransform: 'uppercase',
    },

    button: {
      fontSize: TYPOGRAPHY.fontSize.sm,
      fontWeight: TYPOGRAPHY.fontWeight.medium,
      // Do NOT uppercase — philanthropy apps benefit from a friendly tone
      textTransform: 'none',
      letterSpacing: '0.01em',
    },
  },

  // ---- Shape ------------------------------------------------------------
  shape: {
    borderRadius: SHAPE.borderRadius,
  },

  // ---- Spacing ----------------------------------------------------------
  // MUI spacing(n) = n * spacingUnit
  spacing: SPACING.unit,

  // ---- Transitions -------------------------------------------------------
  transitions: {
    easing: {
      easeInOut: TRANSITION.easing.easeInOut,
      easeOut: TRANSITION.easing.easeOut,
      easeIn: TRANSITION.easing.easeIn,
      sharp: TRANSITION.easing.sharp,
    },
    duration: {
      shortest: TRANSITION.duration.shortest,
      shorter: TRANSITION.duration.shorter,
      short: TRANSITION.duration.short,
      standard: TRANSITION.duration.standard,
      complex: TRANSITION.duration.complex,
      enteringScreen: TRANSITION.duration.enteringScreen,
      leavingScreen: TRANSITION.duration.leavingScreen,
    },
  },

  // ---- Component overrides ----------------------------------------------
  components: {
    // ---- Button ----------------------------------------------------------
    MuiButton: {
      defaultProps: {
        disableElevation: true,
        variant: 'contained',
      },
      styleOverrides: {
        root: {
          textTransform: 'none',
          borderRadius: SHAPE.borderRadius,
          fontWeight: TYPOGRAPHY.fontWeight.medium,
          padding: `${SPACING['1']} ${SPACING['2']}`,
        },
        sizeLarge: {
          padding: `${SPACING['1.5']} ${SPACING['3']}`,
          fontSize: TYPOGRAPHY.fontSize.md,
        },
        sizeSmall: {
          padding: `4px ${SPACING['1']}`,
          fontSize: TYPOGRAPHY.fontSize.xs,
        },
      },
    },

    // ---- TextField -------------------------------------------------------
    MuiTextField: {
      defaultProps: {
        variant: 'outlined',
        size: 'medium',
      },
    },

    // ---- OutlinedInput ---------------------------------------------------
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          borderRadius: SHAPE.borderRadius,
        },
      },
    },

    // ---- Paper -----------------------------------------------------------
    MuiPaper: {
      defaultProps: {
        elevation: 0,
      },
      styleOverrides: {
        root: {
          borderRadius: SHAPE.borderRadius,
          border: `1px solid ${COLOR.divider}`,
        },
        elevation1: {
          boxShadow: '0 1px 3px rgba(0,0,0,0.08), 0 1px 2px rgba(0,0,0,0.06)',
          border: 'none',
        },
        elevation2: {
          boxShadow: '0 4px 6px rgba(0,0,0,0.07), 0 2px 4px rgba(0,0,0,0.06)',
          border: 'none',
        },
      },
    },

    // ---- Card ------------------------------------------------------------
    MuiCard: {
      defaultProps: {
        elevation: 0,
      },
      styleOverrides: {
        root: {
          borderRadius: SHAPE.borderRadiusLg,
          border: `1px solid ${COLOR.divider}`,
          overflow: 'hidden',
        },
      },
    },

    // ---- Chip ------------------------------------------------------------
    MuiChip: {
      styleOverrides: {
        root: {
          borderRadius: SHAPE.borderRadiusSm,
          fontWeight: TYPOGRAPHY.fontWeight.medium,
        },
        label: {
          fontSize: TYPOGRAPHY.fontSize.xs,
        },
      },
    },

    // ---- Avatar ----------------------------------------------------------
    MuiAvatar: {
      styleOverrides: {
        root: {
          backgroundColor: COLOR.primary.lighter,
          color: COLOR.primary.dark,
          fontWeight: TYPOGRAPHY.fontWeight.semibold,
        },
      },
    },

    // ---- AppBar ----------------------------------------------------------
    MuiAppBar: {
      defaultProps: {
        elevation: 0,
        color: 'inherit',
      },
      styleOverrides: {
        root: {
          backgroundColor: COLOR.background.default,
          borderBottom: `1px solid ${COLOR.divider}`,
        },
      },
    },

    // ---- Drawer ----------------------------------------------------------
    MuiDrawer: {
      styleOverrides: {
        paper: {
          border: 'none',
          boxShadow: '4px 0 12px rgba(0,0,0,0.08)',
        },
      },
    },

    // ---- Link ------------------------------------------------------------
    MuiLink: {
      defaultProps: {
        underline: 'hover',
      },
      styleOverrides: {
        root: {
          color: COLOR.primary.main,
          fontWeight: TYPOGRAPHY.fontWeight.medium,
        },
      },
    },

    // ---- Alert -----------------------------------------------------------
    MuiAlert: {
      styleOverrides: {
        root: {
          borderRadius: SHAPE.borderRadius,
        },
      },
    },

    // ---- Skeleton --------------------------------------------------------
    MuiSkeleton: {
      defaultProps: {
        animation: 'wave',
      },
    },

    // ---- Tooltip ---------------------------------------------------------
    MuiTooltip: {
      defaultProps: {
        arrow: true,
      },
      styleOverrides: {
        tooltip: {
          fontSize: TYPOGRAPHY.fontSize.xs,
          borderRadius: SHAPE.borderRadiusSm,
        },
      },
    },

    // ---- Table -----------------------------------------------------------
    MuiTableHead: {
      styleOverrides: {
        root: {
          backgroundColor: COLOR.background.paper,
        },
      },
    },
    MuiTableCell: {
      styleOverrides: {
        head: {
          fontWeight: TYPOGRAPHY.fontWeight.semibold,
          color: COLOR.text.secondary,
          fontSize: TYPOGRAPHY.fontSize.xs,
          textTransform: 'uppercase',
          letterSpacing: '0.05em',
        },
      },
    },

    // ---- CssBaseline ----------------------------------------------------
    MuiCssBaseline: {
      styleOverrides: {
        '*, *::before, *::after': {
          boxSizing: 'border-box',
        },
        body: {
          backgroundColor: COLOR.background.default,
          color: COLOR.text.primary,
          fontFamily: TYPOGRAPHY.fontFamily.primary,
          WebkitFontSmoothing: 'antialiased',
          MozOsxFontSmoothing: 'grayscale',
        },
        ':focus-visible': {
          outline: `2px solid ${COLOR.primary.main}`,
          outlineOffset: '2px',
        },
        'a:focus-visible': {
          borderRadius: SHAPE.borderRadiusSm,
        },
      },
    },
  },
});

// Re-export the Theme type for consumers that need to type-annotate
export type { Theme } from '@mui/material/styles';
